import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { EntityEditTabType } from '../../@types';
import { DEFAULT_TRANSLATION_FIELDS, TRANSLATION_FIELD_CONFIG } from '../../config';
import type { LanguageOption } from '../../config/languages';
import { QUERY_KEYS } from '../../constants';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { useModal } from '../../contexts/ModalContext';
import { useMarketplace } from '../../hooks/useMarketplace';
import useMutateData from '../../hooks/useMutateData';
import { getData } from '../../utils/api';
import FormModalLayout from '../layouts/FormModalLayout';
import { FeedSource } from '../../@types/api';

const styles = {
  accordion: {
    boxShadow: 'none',
    borderBottom: '1px solid',
    borderColor: 'divider',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      m: 0,
    },
  },
  container: {
    minWidth: {
      xs: '100%',
      md: 500,
    },
  },
};

type EntityTranslation = {
  id: string;
  source: FeedSource;
  locales: TranslationItem[];
};

const translationItemSchema = z.object({
  language: z.string(),
  name: z.string().optional(),
  shortName: z.string().optional(),
  restrictionMsgAdmin: z.string().optional(),
  restrictionMsgClient: z.string().optional(),
  verificationMsgAdmin: z.string().optional(),
  verificationMsgClient: z.string().optional(),
  description: z.string().max(500, { message: 'Must be 500 characters or fewer.' }).optional(),
});

type TranslationItem = z.infer<typeof translationItemSchema>;

const schema = z.object({
  locales: z.array(translationItemSchema),
});

type TranslationFormData = z.infer<typeof schema>;

type TranslationTabProps = {
  entity?: EntityEditTabType;
};

const getLocaleEndpoints = ({ entity, id, source }: { entity?: EntityEditTabType; id?: string; source?: string }) => {
  if (!entity || !id) {
    return { getLocalesEndpoint: '', postLocalesEndpoint: '' };
  }

  const baseEndpoint = `locales/${entity}`;
  const sourceQuery = source ? `&source=${source}` : '';

  const getLocalesEndpoint = `${baseEndpoint}?id=${id}${sourceQuery}`;
  const postLocalesEndpoint = `${baseEndpoint}/${id}`;

  return { getLocalesEndpoint, postLocalesEndpoint };
};

const TranslationTab = ({ entity }: TranslationTabProps) => {
  const { item, closeModal } = useModal<{ id: string; source?: string }>();
  const { config } = useMarketplace();

  const { getLocalesEndpoint, postLocalesEndpoint } = getLocaleEndpoints({
    entity,
    id: item?.id,
    source: item?.source,
  });

  const translatableFields = useMemo(
    () => (entity && TRANSLATION_FIELD_CONFIG[entity]) || DEFAULT_TRANSLATION_FIELDS,
    [entity]
  );

  const languageOptions = useFeatureValue<LanguageOption[]>(FEATURE_FLAG.LANGUAGE_OPTIONS, []);
  const supportedLanguages = useMemo(
    () => config?.marketplaceConfig?.languageConfig?.supportedLanguages || [],
    [config]
  );

  const availableLanguages = useMemo(
    () =>
      languageOptions
        .filter((lang) => supportedLanguages.includes(lang.key))
        .reduce(
          (acc, lang) => {
            acc[lang.key] = lang.label;
            return acc;
          },
          {} as Record<string, string>
        ),
    [languageOptions, supportedLanguages]
  );

  const { data: existinglocales } = useQuery({
    queryKey: [QUERY_KEYS.locales, entity, item?.id, item?.source],
    queryFn: (): Promise<EntityTranslation> => getData(getLocalesEndpoint),
    enabled: !!entity && !!item?.id,
    select: (data) => data.locales,
  });

  const formValues = useMemo(() => {
    const initialData = supportedLanguages.map((lang) => {
      const existing = existinglocales?.find((t) => t.language === lang);

      return {
        language: lang,
        ...translatableFields.reduce((acc, { field }) => ({ ...acc, [field]: existing?.[field] || '' }), {}),
      };
    });

    return { locales: initialData };
  }, [supportedLanguages, existinglocales, translatableFields]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TranslationFormData>({
    resolver: zodResolver(schema),
    values: formValues,
  });

  const { fields } = useFieldArray({
    control,
    name: 'locales',
  });

  const { createData } = useMutateData(postLocalesEndpoint, [QUERY_KEYS.locales, entity, item?.id, item?.source]);

  if (!entity) {
    return null;
  }

  const onSubmit = (data: TranslationFormData) => {
    createData({ ...data, source: item?.source }, closeModal);
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onSubmit)}
      onClose={closeModal}
      showEditTabs={false}
      disablePadding
      inputSpacing={0}
    >
      <Box sx={styles.container}>
        {translatableFields.map((translatableField, index) => (
          <Accordion key={translatableField.field} sx={styles.accordion} disableGutters defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1'>{translatableField.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {fields.map((field, index) => {
                  const error = errors.locales?.[index]?.[translatableField.field];

                  return (
                    <TextField
                      key={`${field.id}-${translatableField.field}`}
                      label={availableLanguages[field.language]}
                      fullWidth
                      multiline={translatableField.multiline}
                      error={!!error}
                      helperText={error?.message}
                      {...control.register(`locales.${index}.${translatableField.field}`)}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position='end'>{field.language}</InputAdornment>,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </FormModalLayout>
  );
};

export default TranslationTab;
